import * as React from "react";
import { 
  Mail, Server, CheckCircle, XCircle,
  Loader2, Eye, EyeOff, Key, User, Lock, Hash, Power, Send
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const SmtpSettings = () => {
  const [settings, setSettings] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isTesting, setIsTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const [isEnabled, setIsEnabled] = React.useState(false);
  
  const [host, setHost] = React.useState("");
  const [port, setPort] = React.useState(587);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [fromEmail, setFromEmail] = React.useState("");
  const [fromName, setFromName] = React.useState("Datahub by Endavant");
  const [encryption, setEncryption] = React.useState("tls");
  const [showPassword, setShowPassword] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/smtp/settings`, { withCredentials: true });
      setSettings(response.data);
      setHost(response.data.host || "");
      setPort(response.data.port || 587);
      setUsername(response.data.username || "");
      setFromEmail(response.data.from_email || "");
      setFromName(response.data.from_name || "Datahub by Endavant");
      setEncryption(response.data.encryption || "tls");
      setIsEnabled(response.data.is_enabled === true);
    } catch (error) {
      console.error("Error loading SMTP settings:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    try {
      await axios.put(`${API}/admin/smtp/toggle`, { enabled }, { withCredentials: true });
      setIsEnabled(enabled);
      toast.success(enabled ? "SMTP habilitado" : "SMTP deshabilitado");
    } catch (error) {
      toast.error("Error al cambiar estado de SMTP");
    }
  };

  const handleSave = async () => {
    if (!host || !username || !password || !fromEmail) {
      toast.error("Host, usuario, contraseña y email remitente son requeridos");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/admin/smtp/settings`, {
        host,
        port: parseInt(port),
        username,
        password,
        from_email: fromEmail,
        from_name: fromName,
        encryption
      }, { withCredentials: true });
      
      toast.success("Configuración SMTP guardada");
      setHasChanges(false);
      setPassword("");
      loadSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    
    try {
      // Always send form data if fields are filled
      const hasFormData = host && username && fromEmail;
      
      let response;
      if (hasFormData && password) {
        response = await axios.post(`${API}/admin/smtp/test`, {
          host,
          port: parseInt(port),
          username,
          password,
          from_email: fromEmail,
          from_name: fromName,
          encryption
        }, { withCredentials: true });
      } else if (hasFormData && settings?.is_configured) {
        // Use saved credentials (no new password entered)
        response = await axios.post(`${API}/admin/smtp/test`, null, { withCredentials: true });
      } else {
        toast.error("Completa todos los campos incluyendo la contraseña");
        setIsTesting(false);
        return;
      }
      
      setTestResult(response.data);
      if (response.data.success) {
        toast.success("Email de prueba enviado");
      } else {
        toast.error("Error en la conexión SMTP");
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || "Error al probar la conexión"
      });
      toast.error("Error al probar la conexión");
    } finally {
      setIsTesting(false);
    }
  };

  const handleFieldChange = (setter) => (e) => {
    setter(e.target.value);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enable/Disable Switch */}
      <div className={`flex items-center justify-between p-4 rounded-lg border ${
        isEnabled ? 'bg-indigo-50 border-indigo-200' : 'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          <Power className={`h-5 w-5 ${isEnabled ? 'text-indigo-600' : 'text-slate-400'}`} />
          <div>
            <p className="font-medium text-slate-800">
              {isEnabled ? 'SMTP Habilitado' : 'SMTP Deshabilitado'}
            </p>
            <p className="text-sm text-slate-500">
              {isEnabled 
                ? 'Los módulos pueden enviar correos electrónicos' 
                : 'El envío de correos está desactivado'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          data-testid="smtp-toggle"
        />
      </div>

      {/* Connection Form */}
      <div className={`space-y-4 ${!isEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="flex items-center gap-2 mb-4">
          {settings?.is_configured ? (
            <Badge className="bg-indigo-100 text-indigo-700">Configurado</Badge>
          ) : (
            <Badge variant="outline" className="text-slate-500">No configurado</Badge>
          )}
        </div>

        {/* Server Settings */}
        <div className="grid md:grid-cols-4 gap-4">
          <div className="md:col-span-2 space-y-2">
            <Label htmlFor="smtp-host" className="flex items-center gap-2">
              <Server className="h-4 w-4" />
              Servidor SMTP
            </Label>
            <Input
              id="smtp-host"
              placeholder="smtp-relay.brevo.com"
              value={host}
              onChange={handleFieldChange(setHost)}
              data-testid="smtp-host-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp-port" className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Puerto
            </Label>
            <Input
              id="smtp-port"
              type="number"
              placeholder="587"
              value={port}
              onChange={handleFieldChange(setPort)}
              data-testid="smtp-port-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Encriptación
            </Label>
            <Select value={encryption} onValueChange={(val) => { setEncryption(val); setHasChanges(true); }}>
              <SelectTrigger data-testid="smtp-encryption-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tls">STARTTLS</SelectItem>
                <SelectItem value="ssl">SSL/TLS</SelectItem>
                <SelectItem value="none">Ninguna</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Auth */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="smtp-username" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Usuario
            </Label>
            <Input
              id="smtp-username"
              placeholder="usuario@brevo.com"
              value={username}
              onChange={handleFieldChange(setUsername)}
              data-testid="smtp-username-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="smtp-password" className="flex items-center gap-2">
              <Key className="h-4 w-4" />
              Contraseña / API Key
            </Label>
            <div className="relative">
              <Input
                id="smtp-password"
                type={showPassword ? "text" : "password"}
                placeholder={settings?.is_configured ? "••••••••" : "contraseña o API key"}
                value={password}
                onChange={handleFieldChange(setPassword)}
                className="pr-10"
                data-testid="smtp-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {settings?.is_configured && (
              <p className="text-xs text-slate-500">
                Actual: {settings.password_masked}
              </p>
            )}
          </div>
        </div>

        {/* Sender Info */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="from-email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Remitente
            </Label>
            <Input
              id="from-email"
              type="email"
              placeholder="noreply@tudominio.com"
              value={fromEmail}
              onChange={handleFieldChange(setFromEmail)}
              data-testid="smtp-from-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="from-name" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Nombre Remitente
            </Label>
            <Input
              id="from-name"
              placeholder="Datahub by Endavant"
              value={fromName}
              onChange={handleFieldChange(setFromName)}
              data-testid="smtp-from-name-input"
            />
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <Alert className={testResult.success ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}>
            {testResult.success ? (
              <CheckCircle className="h-4 w-4 text-emerald-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertTitle className={testResult.success ? "text-emerald-800" : "text-red-800"}>
              {testResult.success ? "Conexión Exitosa" : "Error de Conexión"}
            </AlertTitle>
            <AlertDescription className={testResult.success ? "text-emerald-700" : "text-red-700"}>
              {testResult.message}
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={handleTest}
            variant="outline"
            disabled={isTesting || (!settings?.is_configured && !hasChanges)}
            data-testid="smtp-test-btn"
          >
            {isTesting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Enviar Email de Prueba
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="smtp-save-btn"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Lock className="h-4 w-4 mr-2" />
                Guardar Configuración
              </>
            )}
          </Button>
        </div>

        {/* Info */}
        {settings?.last_updated && (
          <p className="text-xs text-slate-500">
            Última actualización: {new Date(settings.last_updated).toLocaleString('es-ES')} 
            {settings.last_updated_by && ` por ${settings.last_updated_by}`}
          </p>
        )}
      </div>
    </div>
  );
};
